import React, { useEffect, useState } from "react";
import { SlidersHorizontal, AlertTriangle, X, Search } from "lucide-react";

const quickFilters = [
  { label: "Family Doctors", value: "family" },
  { label: "Walk-in Clinics", value: "walkin" },
  { label: "Primary Care", value: "primary" },
  { label: "Pediatrics", value: "pediatrics" },
];

const statusOptions = [
  { value: "", label: "Any availability" },
  { value: "accepting", label: "Accepting Patients" },
  { value: "likely_accepting", label: "Likely Accepting" },
  { value: "unknown", label: "Call to Confirm" },
  { value: "not_accepting", label: "Not Accepting" },
];

const sortOptions = [
  { value: "confidence|desc", label: "Confidence (high → low)" },
  { value: "confidence|asc", label: "Confidence (low → high)" },
  { value: "updated|desc", label: "Recently updated" },
  { value: "name|asc", label: "Name (A → Z)" },
];

export default function FilterBar({ meta, values, onApply, onClear, hasFilters }) {
  const [showErModal, setShowErModal] = useState(false);

  const [draft, setDraft] = useState({
    q: values.q || "",
    specialty: values.specialty || "",
    city: values.city || "",
    status: values.status || "",
    sort_by: values.sort_by || "confidence",
    order: values.order || "desc",
  });

  useEffect(() => {
    setDraft({
      q: values.q || "",
      specialty: values.specialty || "",
      city: values.city || "",
      status: values.status || "",
      sort_by: values.sort_by || "confidence",
      order: values.order || "desc",
    });
  }, [
    values.q,
    values.specialty,
    values.city,
    values.status,
    values.sort_by,
    values.order,
  ]);

  const cities = Array.isArray(meta?.cities) ? meta.cities : [];

  const currentSort = `${draft.sort_by || "confidence"}|${
    draft.order || "desc"
  }`;

  const updateDraft = (key, value) => {
    setDraft((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSortChange = (value) => {
    const [sortBy, order] = value.split("|");

    setDraft((prev) => ({
      ...prev,
      sort_by: sortBy,
      order,
    }));
  };

  const applyFilters = () => {
    onApply({
      q: draft.q || "",
      specialty: draft.specialty || "",
      city: draft.city || "",
      status: draft.status || "",
      sort_by: draft.sort_by || "confidence",
      order: draft.order || "desc",
    });
  };

  const clearDraftAndFilters = () => {
    const cleared = {
      q: "",
      specialty: "",
      city: "",
      status: "",
      sort_by: "confidence",
      order: "desc",
    };

    setDraft(cleared);
    onClear();
  };

  const shouldHighlightCity =
    draft.specialty && draft.specialty !== "" && !draft.city;

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 text-slate-500 text-sm font-bold tracking-[0.25em] uppercase mb-4">
        <SlidersHorizontal className="w-4 h-4" />
        Filters
      </div>

      <div className="mb-4 rounded-2xl bg-[#EEF5FB] border border-[#DCEAF5] px-4 py-3 text-sm text-slate-600">
        Choose whether you are looking for a long-term family doctor,
        walk-in clinic, primary care, or pediatrics.
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        {quickFilters.map((f) => {
          const isActive = draft.specialty === f.value;

          return (
            <button
              key={f.value}
              type="button"
              onClick={() =>
                updateDraft("specialty", isActive ? "" : f.value)
              }
              className={`px-5 py-3 rounded-2xl text-sm font-semibold transition-all duration-300 border backdrop-blur-md ${
                isActive
                  ? f.value === "walkin" || f.value === "primary"
                    ? "bg-gradient-to-r from-[#16A34A] to-[#22C55E] text-white border-transparent shadow-[0_8px_24px_rgba(34,197,94,0.35)] scale-[1.02]"
                    : "bg-gradient-to-r from-[#356DFF] to-[#5B8CFF] text-white border-transparent shadow-[0_8px_24px_rgba(53,109,255,0.35)] scale-[1.02]"
                  : "bg-white/80 text-slate-700 border-slate-200 hover:border-[#356DFF] hover:text-[#356DFF]"
              }`}
            >
              {f.label}
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => setShowErModal(true)}
          className="px-5 py-3 rounded-2xl text-sm font-semibold border bg-white/80 text-red-600 border-red-200 hover:bg-red-50"
        >
          <span className="inline-flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            ER Wait Times
          </span>
        </button>
      </div>

      {shouldHighlightCity && (
        <div className="mb-4 rounded-2xl border border-[#B7D4FF] bg-[#EEF5FF] px-4 py-3 text-sm text-[#356DFF] font-medium">
          Next step: select a city, availability, and sorting option. Results
          will update only after clicking Apply Filters.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />

          <input
            type="text"
            placeholder="Search doctor or clinic"
            value={draft.q || ""}
            onChange={(e) => updateDraft("q", e.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 py-3 text-sm text-slate-700 shadow-sm"
          />
        </div>

        <select
          value={draft.city || ""}
          onChange={(e) => updateDraft("city", e.target.value)}
          className={`w-full rounded-2xl bg-white px-4 py-3 text-sm text-slate-700 shadow-sm transition-all duration-300 ${
            shouldHighlightCity
              ? "border-2 border-[#356DFF] ring-4 ring-blue-100"
              : "border border-slate-200"
          }`}
        >
          <option value="">All cities</option>

          {cities.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select
          value={draft.status || ""}
          onChange={(e) => updateDraft("status", e.target.value)}
          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm"
        >
          {statusOptions.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <select
          value={currentSort}
          onChange={(e) => handleSortChange(e.target.value)}
          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm"
        >
          {sortOptions.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-end">
        <button
          type="button"
          onClick={applyFilters}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#356DFF] to-[#5B8CFF] text-white px-6 py-3 text-sm font-bold shadow-lg"
        >
          <Search className="w-4 h-4" />
          Apply Filters
        </button>

        {hasFilters && (
          <button
            type="button"
            onClick={clearDraftAndFilters}
            className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            Clear filters
          </button>
        )}
      </div>

      {showErModal && (
        <div
          className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm overflow-y-auto"
          onClick={() => setShowErModal(false)}
        >
          <div className="min-h-screen flex items-start justify-center px-4 py-10">
            <div
              className="bg-white rounded-[2rem] shadow-2xl max-w-xl w-full p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setShowErModal(false)}
                className="absolute top-5 right-5 bg-white border border-slate-200 rounded-full p-2 shadow"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>

              <div className="w-16 h-16 rounded-2xl bg-[#FFF7ED] border border-[#FED7AA] flex items-center justify-center mb-5">
                <AlertTriangle className="w-8 h-8 text-[#D97706]" />
              </div>

              <h2 className="text-3xl font-black text-slate-900 leading-tight mb-4 pr-12">
                ER Wait Times Coming Soon
              </h2>

              <p className="text-slate-600 leading-7 text-[15px] mb-6">
                We are working on a safe way to show public emergency
                department wait-time information across Ontario hospitals.
              </p>

              <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm leading-6 mb-6">
                For emergencies, call 911 or go to the nearest emergency
                department immediately.
              </div>

              <button
                type="button"
                onClick={() => setShowErModal(false)}
                className="w-full rounded-2xl bg-gradient-to-r from-[#356DFF] to-[#5B8CFF] text-white py-4 font-semibold text-lg shadow-lg"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}